import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Trophy,
  Star,
  Target,
  TrendingUp,
  Users,
  CheckCircle,
  Clock,
  DollarSign,
  Award,
  Briefcase,
  Heart,
  ThumbsUp
} from "lucide-react";
import { Card } from "../components/ui/card";
import SharedHeader5 from "../Headers/SharedHeader5";
import SharedFooter3 from "../Footer/SharedFooter3";
import { getClientProgress } from "../redux/Actions/UserActions";
import { RootState, AppDispatch } from "../redux/store";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";
import { useTranslation } from "react-i18next";

const ProgressPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { clientProgress, clientProgressLoading, clientProgressError } = useSelector(
    (state: RootState) => state.user
  );
  const { user } = useSelector((state: any) => state.auth);
  
  // Get userId from URL params or use current user
  const userId = searchParams.get("userId") || user?.id;

  useEffect(() => {
    if (userId) {
      dispatch(getClientProgress({ userId }));
    }
  }, [dispatch, userId]);

  if (clientProgressLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t("clientProgressPage.loadingProgress")}</p>
        </div>
      </div>
    );
  }

  if (clientProgressError || !clientProgress) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{clientProgressError || t("clientProgressPage.failedToLoad")}</p>
          <button 
            onClick={() => dispatch(getClientProgress({ userId }))} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {t("clientProgressPage.retry")}
          </button>
        </div>
      </div>
    );
  }

  // Map achievements with icons
  const achievementsWithIcons = clientProgress.achievements.map(achievement => ({
    ...achievement,
    icon: achievement.id === 1 ? <Star className="h-6 w-6 text-yellow-500" /> :
          achievement.id === 2 ? <Trophy className="h-6 w-6 text-yellow-500" /> :
          achievement.id === 3 ? <Award className="h-6 w-6 text-yellow-500" /> :
          <ThumbsUp className="h-6 w-6 text-gray-400" />
  }));

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <SharedHeader5 />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t("clientProgressPage.title")}</h1>
          <p className="text-gray-600 mt-1">{t("clientProgressPage.subtitle")}</p>
        </div>

        {/* Level Progress */}
        <Card className="mb-8 p-6 relative overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-3xl font-bold text-blue-600">L{clientProgress.level}</span>
              </div>
            </div>
            
            <div className="flex-1">
              <h2 className="text-xl font-semibold mb-2">{t("clientProgressPage.levelClient", { level: clientProgress.level })}</h2>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${clientProgress.levelProgress}%` }}
                />
              </div>
              <p className="text-gray-600 mb-4">
                {t("clientProgressPage.progressToLevel", { progress: clientProgress.levelProgress, nextLevel: clientProgress.level + 1 })}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {clientProgress.nextLevelRequirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-gray-600">{req}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Briefcase className="h-5 w-5 text-purple-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{t("clientProgressPage.projectsCompleted")}</h3>
            <div className="mt-2">
              <span className="text-2xl font-semibold text-gray-900">
                {clientProgress.completedProjects}
              </span>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-xl font-bold text-blue-600">{getDynamicCurrencySymbol()}</span>
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{t("clientProgressPage.totalSpent")}</h3>
            <div className="mt-2">
              <span className="text-2xl font-semibold text-gray-900">
                {getDynamicCurrencySymbol()}{clientProgress.totalSpent?.toLocaleString() || "0"}
              </span>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{t("clientProgressPage.averageRating")}</h3>
            <div className="mt-2">
              <div className="flex items-center gap-1">
                <span className="text-2xl font-semibold text-gray-900">
                  {clientProgress.averageRating}
                </span>
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${
                        i < Math.floor(clientProgress.averageRating)
                          ? "text-yellow-400 fill-current"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{t("clientProgressPage.reviewsGiven")}</h3>
            <div className="mt-2">
              <span className="text-2xl font-semibold text-gray-900">
                {clientProgress.totalReviews}
              </span>
            </div>
          </Card>
        </div>

        {/* Achievements */}
        <Card className="mb-8">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">{t("clientProgressPage.achievements")}</h2>
            <p className="text-sm text-gray-600">{t("clientProgressPage.achievementsSubtitle")}</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-6">
            {achievementsWithIcons.map((achievement) => (
              <div
                key={achievement.id}
                className={`p-4 rounded-lg border ${
                  achievement.achieved ? "bg-yellow-50 border-yellow-200" : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {achievement.icon}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{achievement.title}</h3>
                    <p className="text-sm text-gray-600">{achievement.description}</p>
                    {!achievement.achieved && achievement.progress && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-600 h-1.5 rounded-full"
                            style={{ width: `${(achievement.progress / 10) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {achievement.progress}/10 completed
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Milestones */}
        <Card>
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">{t("clientProgressPage.recentMilestones")}</h2>
            <p className="text-sm text-gray-600">{t("clientProgressPage.recentMilestonesSubtitle")}</p>
          </div>
          
          <div className="divide-y">
            {clientProgress.recentMilestones.map((milestone, index) => (
              <div key={index} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Trophy className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">{milestone.title}</h3>
                    <p className="text-sm text-gray-600">{milestone.description}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(milestone.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </main>

      <div className="h-32"></div>
      <SharedFooter3 />
    </div>
  );
};

export default ProgressPage;